import React, { Component, createRef } from "react";
import { Button, Spin, message } from "antd";
import { PrinterOutlined, DownloadOutlined } from "@ant-design/icons";
import JsBarcode from "jsbarcode";
import { generateRequisitionPDF } from "./generateRequisitionPDF";

class RequisitionPDF extends Component {
  constructor(props) {
    super(props);
    this.barcodeRef = createRef();
    this.state = {
      generating: false,
    };
  }

  componentDidMount() {
    this.generateBarcode();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.requisition?.requisitionID !== this.props.requisition?.requisitionID) {
      this.generateBarcode();
    }
  }

  generateBarcode = () => {
    const { requisition } = this.props;
    if (this.barcodeRef.current && requisition?.requisitionID) {
      JsBarcode(this.barcodeRef.current, String(requisition.requisitionID), {
        format: "CODE128",
        width: 1.5,
        height: 40,
        displayValue: false,
        margin: 0,
      });
    }
  };

  handleDownloadPDF = async () => {
    const { requisition, logoBase64 } = this.props;
    this.setState({ generating: true });
    try {
      const doc = generateRequisitionPDF(requisition, logoBase64);
      doc.save(`Requisition_${requisition.requisitionID}.pdf`);
      message.success("PDF downloaded successfully");
    } catch (err) {
      console.error("PDF generation error:", err);
      message.error("Failed to generate PDF");
    } finally {
      this.setState({ generating: false });
    }
  };

  handlePrintPDF = async () => {
    const { requisition, logoBase64 } = this.props;
    this.setState({ generating: true });
    try {
      const doc = generateRequisitionPDF(requisition, logoBase64);
      const blobURL = doc.output("bloburl");
      window.open(blobURL, "_blank");
    } catch (err) {
      console.error("PDF generation error:", err);
      message.error("Failed to generate PDF");
    } finally {
      this.setState({ generating: false });
    }
  };

  renderDiagnoses() {
    const { requisition } = this.props;
    const diagnoses = requisition.diagnoses || [];
    const half = Math.ceil(diagnoses.length / 2);
    const leftCol = diagnoses.slice(0, half);
    const rightCol = diagnoses.slice(half);

    return (
      <table style={{ fontSize: "10px", width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ width: "50%", verticalAlign: "top", paddingRight: "4px" }}>
              <table style={styles.patientBox}>
                <tbody>
                  {leftCol.map((d, i) => (
                    <tr key={i}>
                      <td style={{ width: "22%" }}>{d.code}</td>
                      <td>{d.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
            <td style={{ width: "50%", verticalAlign: "top", paddingLeft: "4px" }}>
              {rightCol.length > 0 && (
                <table style={styles.patientBox}>
                  <tbody>
                    {rightCol.map((d, i) => (
                      <tr key={i}>
                        <td style={{ width: "22%" }}>{d.code}</td>
                        <td>{d.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderTests() {
    const { requisition } = this.props;
    const tests = requisition.tests || [];
    const half = Math.ceil(tests.length / 2);
    const leftCol = tests.slice(0, half);
    const rightCol = tests.slice(half);

    const testTable = (items) => (
      <table style={{ ...styles.testsTable, fontSize: "10px", width: "100%" }}>
        <thead>
          <tr>
            <th style={styles.testsTableTh}>Profile/Test</th>
            <th style={styles.testsTableTh}>Profile/Test Description</th>
          </tr>
        </thead>
        <tbody>
          {items.map((t, i) => (
            <tr key={i}>
              <td style={{ ...styles.testsTableTd, width: "30%" }}>{t.code || ""}</td>
              <td style={styles.testsTableTd}>{t.description || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

    return (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <td style={{ width: "50%", verticalAlign: "top", paddingRight: "4px" }}>
              {testTable(leftCol)}
            </td>
            <td style={{ width: "50%", verticalAlign: "top", paddingLeft: "4px" }}>
              {rightCol.length > 0 && testTable(rightCol)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  renderInsurance() {
    const { requisition } = this.props;
    if (requisition.billTo === "Insurance") {
      const insurances = requisition.insurances || [];
      return (
        <table style={styles.patientBox}>
          <tbody>
            <tr>
              <td><strong>Bill To:</strong></td>
              <td colSpan={7}>{requisition.billTo}</td>
            </tr>
            {insurances.map((ins, i) => (
              <tr key={i}>
                <td style={{ width: "12%" }}>
                  <strong>{ins.isPrimary ? "Primary:" : "Secondary:"}</strong>
                </td>
                <td style={{ width: "30%" }}>
                  {ins.insCode} - {ins.insurance}
                </td>
                <td style={{ width: "16%" }}>
                  <strong>Relation to insured:</strong>
                </td>
                <td style={{ width: "8%" }}>{ins.relationToInsured}</td>
                <td style={{ width: "5%" }}>
                  <strong>Policy:</strong>
                </td>
                <td style={{ width: "15%" }}>{ins.memberID}</td>
                <td style={{ width: "5%" }}>
                  <strong>Group:</strong>
                </td>
                <td>{ins.groupID}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <table style={styles.patientBox}>
        <tbody>
          <tr>
            <td style={{ width: "12%" }}>
              <strong>Bill To:</strong>
            </td>
            <td>{requisition.billTo}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  render() {
    const { requisition, logoBase64 } = this.props;
    const { generating } = this.state;

    if (!requisition) return null;

    const physician = requisition.physician || {};
    const client = requisition.client || {};

    return (
      <div>
        <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={this.handleDownloadPDF}
            loading={generating}
          >
            Download PDF
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={this.handlePrintPDF}
            loading={generating}
          >
            Print
          </Button>
        </div>

        <div style={{ height: "65vh", overflowY: "auto" }}>
          {generating && (
            <div style={{ textAlign: "center", padding: 16 }}>
              <Spin tip="Generating PDF..." />
            </div>
          )}

          {/* ===== On-screen preview ===== */}
          <div style={styles.pdfBody}>
            {/* HEADER */}
            <div style={styles.header}>
              <div style={styles.logoSection}>
                {logoBase64 && (
                  <img src={logoBase64} alt="Lab Logo" style={{ width: 160, height: 60 }} />
                )}
                <div style={styles.companyName}>Lincoln Reference Laboratory</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <table>
                  <tbody>
                    <tr>
                      <td colSpan={2}>
                        <svg ref={this.barcodeRef} style={{ width: 140, height: 40 }} />
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Requisition #:</strong></td>
                      <td>&nbsp;&nbsp;&nbsp;{requisition.requisitionID}</td>
                    </tr>
                    <tr>
                      <td><strong>Patient #:</strong></td>
                      <td>&nbsp;&nbsp;&nbsp;{requisition.chartNumber}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* CLIENT + COLLECTION INFO */}
            <table style={styles.baseTable}>
              <tbody>
                <tr>
                  <td>
                    <table>
                      <tbody>
                        <tr>
                          <td style={{ width: "20%" }}><strong>Client:</strong></td>
                          <td>{requisition.clientCode} - {client.name || ""}</td>
                        </tr>
                        <tr><td /><td>{client.address} {client.address1}</td></tr>
                        <tr><td /><td>{client.city}, {client.state} {client.zip}</td></tr>
                        <tr><td>&nbsp;</td><td /></tr>
                        <tr>
                          <td><strong>Phone:</strong></td>
                          <td>{client.phone || ""}</td>
                        </tr>
                        <tr>
                          <td><strong>Ref. Phys:</strong></td>
                          <td>
                            {physician.lastName
                              ? `${physician.lastName}, ${physician.firstName} ${physician.middleName || ""} (${physician.npi || ""})`
                              : ""}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                  <td>
                    <table>
                      <tbody>
                        <tr>
                          <td style={{ width: "45%" }}><strong>Collection Date:</strong></td>
                          <td>{requisition.collectionDate || ""}</td>
                        </tr>
                        <tr>
                          <td><strong>Collection Time:</strong></td>
                          <td>{requisition.collectionTime || ""}</td>
                        </tr>
                        <tr>
                          <td><strong>Fasting:</strong></td>
                          <td>{requisition.isFasting ? "Yes" : "No"}</td>
                        </tr>
                        <tr>
                          <td><strong>Order Type:</strong></td>
                          <td>
                            {requisition.orderType === "Stat" ? (
                              <strong style={{ fontSize: 15 }}>STAT</strong>
                            ) : (requisition.orderType)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* PATIENT INFO */}
            <table style={styles.reqInfo}>
              <tbody>
                <tr style={{ border: "1px solid #000" }}>
                  <td>
                    <table style={styles.patientBox}>
                      <tbody>
                        <tr>
                          <td>
                            <table>
                              <tbody>
                                <tr>
                                  <td style={{ width: "26%" }}><strong>Patient Name:</strong></td>
                                  <td>{requisition.lastName}, {requisition.firstName}</td>
                                </tr>
                                <tr><td /><td>{requisition.homeAddress}</td></tr>
                                <tr>
                                  <td>&nbsp;</td>
                                  <td>{requisition.homeCity} {requisition.homeState}, {requisition.homeZip}</td>
                                </tr>
                                <tr><td>&nbsp;</td></tr>
                                <tr>
                                  <td><strong>Birth Date:</strong></td>
                                  <td style={{ width: "40%" }}>{requisition.dob}</td>
                                  <td style={{ width: "15%" }}><strong>Sex:</strong></td>
                                  <td>{requisition.sex}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          <td>
                            <table>
                              <tbody>
                                <tr>
                                  <td style={{ width: "15%" }}><strong>Phone:</strong></td>
                                  <td>{requisition.phone}</td>
                                </tr>
                                <tr>
                                  <td><strong>Email:</strong></td>
                                  <td>{requisition.email}</td>
                                </tr>
                                <tr><td>&nbsp;</td></tr>
                                <tr><td>&nbsp;</td></tr>
                                <tr>
                                  <td><strong>Race:</strong></td>
                                  <td style={{ width: "25%" }}>{requisition.race || "Unknown"}</td>
                                  <td style={{ width: "20%" }}><strong>Ethnicity:</strong></td>
                                  <td>{requisition.ethnicity || "Unknown"}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr style={{ border: "1px solid #000" }}>
                  <td>
                    <table style={styles.patientBox}>
                      <tbody>
                        <tr><td>{this.renderInsurance()}</td></tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr style={{ border: "1px solid #000" }}>
                  <td>
                    <table style={styles.patientBox}>
                      <tbody>
                        <tr>
                          <td style={{ width: "12%" }}><strong>Clinical Info:</strong></td>
                          <td>{requisition.clinicalInformation}</td>
                        </tr>
                        <tr>
                          <td><strong>Comments:</strong></td>
                          <td>{requisition.comments}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* DIAGNOSES */}
            <div style={{ marginTop: 4 }}>
              <strong style={{ fontSize: 10 }}>Diagnosis:</strong>
              {this.renderDiagnoses()}
            </div>

            {/* TESTS */}
            <div style={{ marginTop: 2 }}>{this.renderTests()}</div>

            {/* SIGNATURE */}
            <div style={styles.signatureSection}>
              <table style={{ width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "30%" }}>&nbsp;</td>
                    <td style={{ width: "20%" }}>&nbsp;</td>
                    <td style={{ width: "30%" }}>&nbsp;</td>
                    <td style={{ width: "20%" }}>&nbsp;</td>
                  </tr>
                  <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td style={styles.signatureLine}>
                      Electronically signed by {physician.fullName}
                    </td>
                    <td style={styles.signatureLine}>
                      {new Date().toLocaleDateString("en-US")}
                    </td>
                  </tr>
                  <tr>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>Physician Signature</td>
                    <td>Date</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  pdfBody: {
    fontFamily: "Arial, sans-serif",
    fontSize: 9,
    padding: 20,
    backgroundColor: "#fff",
    color: "#000",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 5,
    borderBottom: "1px solid #000",
    paddingBottom: 5,
  },
  logoSection: { display: "flex", alignItems: "center", gap: 10 },
  companyName: { fontSize: 10, fontWeight: "bold" },
  baseTable: { width: "100%", borderSpacing: 0, borderCollapse: "collapse" },
  reqInfo: { width: "100%", borderCollapse: "collapse" },
  patientBox: { width: "100%", borderCollapse: "collapse", marginBottom: 1, border: "0px" },
  testsTable: { width: "100%", borderCollapse: "collapse", marginBottom: 2 },
  testsTableTh: {
    backgroundColor: "#f0f0f0",
    padding: 4,
    textAlign: "left",
    fontWeight: "bold",
    border: "1px solid #000",
  },
  testsTableTd: { padding: 4, border: "1px solid #000" },
  signatureSection: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 60,
    marginTop: 60,
  },
  signatureLine: { borderBottom: "1px solid #000" },
};

export default RequisitionPDF;
